<!--
  Potential clients of ONE product — opened from the stock page and from "Potential interest".

  Five independent signals, each in its own tab, plus a summary that merges them:
    - Bought & paid        : invoices of this product (paid = sale status "Paid");
    - Quoted, not ordered  : quotations (pending or cancelled) never turned into an invoice;
    - Bought together      : clients who buy what this product's buyers also buy
                             (item-to-item collaborative filtering, cosine similarity);
    - Similar products     : clients who bought a neighbouring product (same category or
                             family, close dimensions) but never this one;
    - Same area            : active clients located where this product sells (FR department,
                             BE province, RO county...).
  Rows ticked in any tab form one selection; "Send an offer" opens the composer
  (administrators only).
-->
<template>
  <vs-popup fullscreen :title="popupTitle" :active.sync="isActive" class="pi-clients-popup">
    <div class="pi-clients">
      <div v-if="loading" class="mb-4">Computing potential clients...</div>
      <vs-alert v-if="error" color="danger" active="true" class="mb-4">{{ error }}</vs-alert>

      <template v-if="data && !loading">
        <!-- Product header -->
        <div class="flex flex-wrap items-start mb-3">
          <div class="flex-grow mr-4 mb-2">
            <h4 class="mb-1">{{ data.product.category }} — {{ data.product.product }}</h4>
            <div class="text-sm opacity-75">
              #{{ data.product.id }} · stock {{ qty(data.product.current_qty) }} {{ data.product.uom }}
              · projected {{ qty(data.product.projected_qty) }} {{ data.product.uom }}
              <span v-if="data.product.volume"> ({{ qty(data.product.projected_qty * data.product.volume, 2) }} m³)</span>
            </div>
          </div>
          <div class="text-sm pi-legend mb-2">
            Score = all signals combined: bought (recent, regular, reorder due) &gt; quoted &gt;
            bought together &gt; similar product &gt; same area. Hover a chip for details.
          </div>
        </div>

        <!-- Tabs -->
        <div class="flex flex-wrap items-center mb-3">
          <vs-button v-for="t in tabs" :key="t.key" size="small" class="mr-2 mb-2"
            :type="tab === t.key ? 'filled' : 'border'" :color="t.color" @click="tab = t.key">
            {{ t.label }} ({{ t.count }})
          </vs-button>
        </div>

        <!-- Toolbar -->
        <div class="flex flex-wrap items-center mb-3">
          <vs-input class="mr-4 mb-2" icon-pack="feather" icon="icon-search" icon-no-border
            v-model="search" placeholder="Filter client or country..." />
          <vs-checkbox v-model="onlyWithEmail" class="mr-4 mb-2">Only clients with an e-mail</vs-checkbox>
          <vs-checkbox v-if="tab === 'buyers'" v-model="includeUnpaid" class="mr-4 mb-2">Include unpaid orders</vs-checkbox>
          <vs-checkbox v-if="tab === 'quotes'" v-model="includeBoughtQuotes" class="mr-4 mb-2">Include clients who bought it since</vs-checkbox>
          <div class="flex-grow"></div>
          <vs-button v-if="tab !== 'offers'" size="small" type="border" class="mr-2 mb-2" @click="selectVisible(true)">
            Tick visible
          </vs-button>
          <vs-button size="small" type="border" class="mr-2 mb-2" @click="exportXlsx">Export XLSX</vs-button>
        </div>

        <div class="pi-scroll">
          <!-- SUMMARY -->
          <table v-if="tab === 'summary'" class="pi-table w-full">
            <thead>
              <tr>
                <th style="width: 40px"></th><th>Client</th><th>Country</th><th>Lang</th>
                <th style="width: 150px">Score</th><th>Signals</th><th>Last activity</th><th>Last offer</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in pageRows" :key="r.idperson">
                <td><vs-checkbox :value="!!selected[r.idperson]" :disabled="!selectable(r)" @input="toggle(r, bestReason(r), $event)" /></td>
                <td class="pi-name">
                  <a :href="personUrl(r)" target="_blank" rel="noopener">{{ r.name }}</a>
                  <contact-flags :row="r" />
                </td>
                <td>{{ r.country_code || '—' }}</td>
                <td>{{ (r.lang || '').toUpperCase() }}</td>
                <td>
                  <div class="pi-bar"><div class="pi-bar-fill" :style="{ width: pct(r.score) }"></div></div>
                  <span class="text-xs">{{ pct(r.score) }}</span>
                </td>
                <td class="pi-signals">
                  <vs-chip v-if="r.signals.bought" color="success"
                    :title="`${r.signals.bought.n_invoices} invoice(s), ${r.signals.bought.n_paid} paid, ${qty(r.signals.bought.qty)} ${uom} — last ${r.signals.bought.last_date}`">
                    Bought ×{{ r.signals.bought.n_invoices }}
                  </vs-chip>
                  <vs-chip v-if="r.signals.bought && r.signals.bought.reorder_status === 'due'" color="danger">Reorder due</vs-chip>
                  <vs-chip v-if="r.signals.quoted" color="warning"
                    :title="`${r.signals.quoted.n_quotes} quotation(s), ${qty(r.signals.quoted.qty)} ${uom} — last ${r.signals.quoted.last_date}`">
                    Quoted ×{{ r.signals.quoted.n_quotes }}
                  </vs-chip>
                  <vs-chip v-if="r.signals.related" color="primary" :title="reasonsTitle(r.signals.related.reasons)">
                    Bought together {{ pct(r.signals.related.relative) }}
                  </vs-chip>
                  <vs-chip v-if="r.signals.similar" color="primary"
                    :title="r.signals.similar.best ? `${r.signals.similar.best.category} ${r.signals.similar.best.name}` : ''">
                    Similar {{ pct(r.signals.similar.best_sim) }}
                  </vs-chip>
                  <vs-chip v-if="r.signals.area" color="dark"
                    :title="`${r.signals.area.buyers_in_area} buyer(s) of this product in ${r.signals.area.area}`">
                    Area {{ r.signals.area.area }}
                  </vs-chip>
                  <vs-chip v-if="r.signals.open_order" color="danger" title="Approved quotation not invoiced yet">Order in progress</vs-chip>
                </td>
                <td>{{ r.last_date || '—' }}</td>
                <td>{{ r.last_offer_at || '—' }}</td>
              </tr>
            </tbody>
          </table>

          <!-- BUYERS -->
          <table v-else-if="tab === 'buyers'" class="pi-table w-full">
            <thead>
              <tr>
                <th style="width: 40px"></th><th>Client</th><th>Country</th>
                <th class="text-right">Invoices (paid)</th><th class="text-right">Qty ({{ uom }})</th>
                <th class="text-right">Amount (EUR)</th><th class="text-right">Last price</th>
                <th>First</th><th>Last</th><th>Cadence</th><th>Last offer</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in pageRows" :key="r.idperson">
                <td><vs-checkbox :value="!!selected[r.idperson]" :disabled="!selectable(r)" @input="toggle(r, 'bought', $event)" /></td>
                <td class="pi-name">
                  <a :href="personUrl(r)" target="_blank" rel="noopener">{{ r.name }}</a>
                  <contact-flags :row="r" />
                  <vs-chip v-if="r.open_order" color="danger">Order in progress</vs-chip>
                </td>
                <td>{{ r.country_code || '—' }}</td>
                <td class="text-right">{{ r.n_invoices }} ({{ r.n_paid }})</td>
                <td class="text-right">{{ qty(r.qty) }}</td>
                <td class="text-right">{{ money(r.amount) }}</td>
                <td class="text-right">
                  <a v-if="r.last_invoice_id" :href="'/web/invoice/' + r.last_invoice_id" target="_blank" rel="noopener"
                    :title="r.last_invoice_no">{{ money(r.last_price) }}</a>
                </td>
                <td>{{ r.first_date }}</td>
                <td>{{ r.last_date }}</td>
                <td>
                  <span v-if="r.reorder_interval_days">every ~{{ r.reorder_interval_days }} d</span>
                  <vs-chip v-if="r.reorder_status === 'due'" color="danger">Reorder due</vs-chip>
                  <vs-chip v-else-if="r.reorder_status === 'lapsed'" color="dark">Lapsed</vs-chip>
                </td>
                <td>{{ r.last_offer_at || '—' }}</td>
              </tr>
            </tbody>
          </table>

          <!-- QUOTES -->
          <table v-else-if="tab === 'quotes'" class="pi-table w-full">
            <thead>
              <tr>
                <th style="width: 40px"></th><th>Client</th><th>Country</th>
                <th class="text-right">Quotations</th><th class="text-right">Qty asked ({{ uom }})</th>
                <th>Last quotation</th><th>Status</th><th class="text-right">Quoted price</th>
                <th>Bought since?</th><th>Last offer</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in pageRows" :key="r.idperson">
                <td><vs-checkbox :value="!!selected[r.idperson]" :disabled="!selectable(r)" @input="toggle(r, 'quoted', $event)" /></td>
                <td class="pi-name">
                  <a :href="personUrl(r)" target="_blank" rel="noopener">{{ r.name }}</a>
                  <contact-flags :row="r" />
                </td>
                <td>{{ r.country_code || '—' }}</td>
                <td class="text-right">{{ r.n_quotes }}</td>
                <td class="text-right">{{ qty(r.qty) }}</td>
                <td>
                  <a :href="'/web/quotation/' + r.last_quote_id" target="_blank" rel="noopener">#{{ r.last_quote_id }}</a>
                  {{ r.last_date }}
                </td>
                <td>
                  <vs-chip :color="r.last_status === 3 ? 'dark' : 'warning'">{{ r.last_status === 3 ? 'Cancelled' : 'Pending' }}</vs-chip>
                </td>
                <td class="text-right">{{ money(r.last_price) }}</td>
                <td>
                  <vs-chip v-if="r.bought" color="success">Bought</vs-chip>
                  <vs-chip v-if="r.open_order" color="danger">Order in progress</vs-chip>
                  <span v-if="!r.bought && !r.open_order">No</span>
                </td>
                <td>{{ r.last_offer_at || '—' }}</td>
              </tr>
            </tbody>
          </table>

          <!-- RELATED -->
          <table v-else-if="tab === 'related'" class="pi-table w-full">
            <thead>
              <tr>
                <th style="width: 40px"></th><th>Client</th><th>Country</th><th style="width: 150px">Affinity</th>
                <th>Because they bought</th><th class="text-right">Related products</th><th>Last offer</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in pageRows" :key="r.idperson">
                <td><vs-checkbox :value="!!selected[r.idperson]" :disabled="!selectable(r)" @input="toggle(r, 'related', $event)" /></td>
                <td class="pi-name">
                  <a :href="personUrl(r)" target="_blank" rel="noopener">{{ r.name }}</a>
                  <contact-flags :row="r" />
                </td>
                <td>{{ r.country_code || '—' }}</td>
                <td>
                  <div class="pi-bar"><div class="pi-bar-fill" :style="{ width: pct(r.relative) }"></div></div>
                  <span class="text-xs">{{ pct(r.relative) }}</span>
                </td>
                <td class="text-sm">
                  <div v-for="x in r.reasons" :key="x.id">
                    <a :href="'/web/stock/' + x.id" target="_blank" rel="noopener">{{ x.category }} {{ x.name }}</a>
                    <span class="opacity-50">({{ pct(x.sim) }})</span>
                  </div>
                </td>
                <td class="text-right">{{ r.n_reasons }}</td>
                <td>{{ r.last_offer_at || '—' }}</td>
              </tr>
            </tbody>
          </table>

          <!-- SIMILAR -->
          <table v-else-if="tab === 'similar'" class="pi-table w-full">
            <thead>
              <tr>
                <th style="width: 40px"></th><th>Client</th><th>Country</th><th class="text-right">Similarity</th>
                <th>Closest product bought</th><th class="text-right">Similar products</th>
                <th class="text-right">Qty</th><th>Last purchase</th><th>Last offer</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in pageRows" :key="r.idperson">
                <td><vs-checkbox :value="!!selected[r.idperson]" :disabled="!selectable(r)" @input="toggle(r, 'similar', $event)" /></td>
                <td class="pi-name">
                  <a :href="personUrl(r)" target="_blank" rel="noopener">{{ r.name }}</a>
                  <contact-flags :row="r" />
                </td>
                <td>{{ r.country_code || '—' }}</td>
                <td class="text-right">{{ pct(r.best_sim) }}</td>
                <td class="text-sm">
                  <a v-if="r.best" :href="'/web/stock/' + r.best.id" target="_blank" rel="noopener">{{ r.best.category }} {{ r.best.name }}</a>
                </td>
                <td class="text-right">{{ r.n_similar }}</td>
                <td class="text-right">{{ qty(r.qty) }}</td>
                <td>{{ r.last_date }}</td>
                <td>{{ r.last_offer_at || '—' }}</td>
              </tr>
            </tbody>
          </table>

          <!-- AREA -->
          <table v-else-if="tab === 'area'" class="pi-table w-full">
            <thead>
              <tr>
                <th style="width: 40px"></th><th>Client</th><th>Country</th><th>Area</th>
                <th class="text-right">Buyers of this product in the area</th><th>Last activity</th><th>Last offer</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in pageRows" :key="r.idperson">
                <td><vs-checkbox :value="!!selected[r.idperson]" :disabled="!selectable(r)" @input="toggle(r, 'area', $event)" /></td>
                <td class="pi-name">
                  <a :href="personUrl(r)" target="_blank" rel="noopener">{{ r.name }}</a>
                  <contact-flags :row="r" />
                </td>
                <td>{{ r.country_code || '—' }}</td>
                <td>{{ r.area }}</td>
                <td class="text-right">{{ r.buyers_in_area }}</td>
                <td>{{ r.last_activity }}</td>
                <td>{{ r.last_offer_at || '—' }}</td>
              </tr>
            </tbody>
          </table>

          <!-- OFFERS SENT -->
          <table v-else-if="tab === 'offers'" class="pi-table w-full">
            <thead>
              <tr>
                <th>Sent</th><th>Client</th><th>To</th><th>Lang</th><th>Zone</th>
                <th class="text-right">Price</th><th>Why</th><th>Status</th><th>By</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="o in pageRows" :key="o.id">
                <td>{{ o.sent_at }}</td>
                <td><a :href="'/web/person/' + o.idperson" target="_blank" rel="noopener">{{ o.name }}</a></td>
                <td class="text-sm pi-emails">{{ o.to_addr }}</td>
                <td>{{ (o.language || '').toUpperCase() }}</td>
                <td>{{ o.price_zone }}</td>
                <td class="text-right">{{ o.unit_price != null ? money(o.unit_price) : '—' }} <span class="text-xs opacity-75">{{ o.price_unit }}</span></td>
                <td>{{ reasonLabel(o.reason) }}</td>
                <td :class="o.status === 'sent' ? 'text-success' : 'text-danger'" :title="o.error">{{ o.status }}</td>
                <td>{{ o.sent_by }}</td>
              </tr>
            </tbody>
          </table>

          <div v-if="!filteredRows.length" class="p-4 opacity-75">
            {{ emptyMessage }}
          </div>
          <div v-if="filteredRows.length > pageSize" class="p-3 text-center">
            <vs-button size="small" type="flat" @click="pageSize += 100">
              Show more ({{ filteredRows.length - pageSize }} left)
            </vs-button>
          </div>
        </div>

        <!-- Selection bar -->
        <div class="pi-selection-bar flex flex-wrap items-center">
          <span class="mr-4"><b>{{ selectedCount }}</b> client(s) ticked</span>
          <vs-button size="small" type="border" class="mr-2" :disabled="!selectedCount" @click="clearSelection">Clear</vs-button>
          <div class="flex-grow"></div>
          <span v-if="!isAdmin" class="text-sm opacity-75 mr-2">Sending offers is reserved to administrators.</span>
          <vs-button icon-pack="feather" icon="icon-mail" :disabled="!selectedCount || !isAdmin" @click="openComposer">
            Send an offer to {{ selectedCount }} client(s)
          </vs-button>
        </div>
      </template>
    </div>

    <offer-mail-composer v-if="composerOpen" :active.sync="composerOpen" :idproduct="idproduct"
      :selection="composerSelection" @sent="onSent" />
  </vs-popup>
</template>

<script>
import axios from '@/axios.js'
import * as XLSX from 'xlsx'
import OfferMailComposer from './OfferMailComposer.vue'
import { REASON_LABELS } from './offerTemplates'

// Badges d'un contact : e-mail absent, désabonné, langue ou zone déduites.
const ContactFlags = {
  name: 'ContactFlags',
  functional: true,
  props: { row: { type: Object, required: true } },
  render (h, { props }) {
    const r = props.row
    const out = []
    if (r.unsubscribed) out.push(h('span', { class: 'pi-flag pi-flag--danger', attrs: { title: 'Unsubscribed from mailings' } }, 'unsubscribed'))
    if (!r.has_email) out.push(h('span', { class: 'pi-flag pi-flag--danger', attrs: { title: 'No e-mail on the contact record' } }, 'no e-mail'))
    if (r.isbroker) out.push(h('span', { class: 'pi-flag', attrs: { title: 'Broker record' } }, 'broker'))
    return out.length ? h('div', out) : null
  }
}

const REASON_ORDER = ['bought', 'quoted', 'related', 'similar', 'area']

export default {
  name: 'PotentialClientsPopup',
  components: { OfferMailComposer, ContactFlags },
  props: {
    active: { type: Boolean, default: false },
    idproduct: { type: [Number, String], default: null }
  },
  data () {
    return {
      loading: false,
      error: null,
      data: null,
      offers: [],
      tab: 'summary',
      search: '',
      onlyWithEmail: false,
      includeUnpaid: false,
      includeBoughtQuotes: true,
      pageSize: 100,
      selected: {},
      composerOpen: false,
      composerSelection: []
    }
  },
  computed: {
    isActive: {
      get () { return this.active },
      set (val) { this.$emit('update:active', val) }
    },
    isAdmin () {
      return (localStorage.getItem('login-roles') || '').toLowerCase().includes('admin')
    },
    popupTitle () {
      return this.data ? `Potential clients — ${this.data.product.category} ${this.data.product.product}` : 'Potential clients'
    },
    uom () {
      return (this.data && this.data.product.uom) || ''
    },
    tabs () {
      const m = (this.data && this.data.methods) || {}
      const c = (k) => (m[k] || []).length
      return [
        { key: 'summary', label: 'Summary', count: this.data ? this.data.summary.length : 0, color: 'primary' },
        { key: 'buyers', label: this.includeUnpaid ? 'Bought' : 'Bought & paid', count: this.includeUnpaid ? c('buyers') : (this.data ? this.data.counts.buyers_paid : 0), color: 'success' },
        { key: 'quotes', label: 'Quoted, not ordered', count: c('quotes'), color: 'warning' },
        { key: 'related', label: 'Bought together', count: c('related'), color: 'primary' },
        { key: 'similar', label: 'Similar products', count: c('similar'), color: 'primary' },
        { key: 'area', label: 'Same area', count: c('area'), color: 'dark' },
        { key: 'offers', label: 'Offers sent', count: this.offers.length, color: 'dark' }
      ]
    },
    rows () {
      if (!this.data) return []
      if (this.tab === 'summary') return this.data.summary
      if (this.tab === 'offers') return this.offers
      const list = this.data.methods[this.tab] || []
      if (this.tab === 'buyers' && !this.includeUnpaid) return list.filter(r => r.n_paid > 0)
      if (this.tab === 'quotes' && !this.includeBoughtQuotes) return list.filter(r => !r.bought)
      return list
    },
    filteredRows () {
      const q = this.search.trim().toLowerCase()
      return this.rows.filter(r => {
        if (this.onlyWithEmail && this.tab !== 'offers' && !r.has_email) return false
        if (!q) return true
        return `${r.name || ''} ${r.country_code || ''} ${r.area || ''}`.toLowerCase().includes(q)
      })
    },
    pageRows () {
      return this.filteredRows.slice(0, this.pageSize)
    },
    selectedCount () {
      return Object.keys(this.selected).length
    },
    emptyMessage () {
      const msg = {
        summary: 'No potential client found for this product.',
        buyers: 'Nobody has bought and paid this product yet.',
        quotes: 'No quotation for this product was left without an order.',
        related: 'Not enough buyers to find products bought together.',
        similar: 'No client bought a similar product without buying this one.',
        area: 'This product\'s buyers are too scattered to define an area (at least 2 buyers in the same area).',
        offers: 'No offer sent for this product yet.'
      }
      return this.search ? 'No row matches the filter.' : msg[this.tab]
    }
  },
  watch: {
    active: {
      immediate: true,
      handler (val) { if (val) this.load() }
    },
    tab () { this.pageSize = 100 },
    idproduct () { if (this.active) this.load() }
  },
  methods: {
    qty (value, digits = 0) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return n.toLocaleString('en-US', { maximumFractionDigits: digits })
    },
    money (value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    pct (x) {
      const n = Number(x)
      return Number.isFinite(n) ? `${Math.round(n * 100)}%` : '—'
    },
    personUrl (r) { return `/web/person/${r.idperson}` },
    reasonLabel (reason) { return REASON_LABELS[reason] || reason || '—' },
    reasonsTitle (reasons) {
      return (reasons || []).map(x => `${x.category} ${x.name} (${this.pct(x.sim)})`).join('\n')
    },
    selectable (r) {
      return r.has_email && !r.unsubscribed
    },
    bestReason (r) {
      return REASON_ORDER.find(k => r.signals && r.signals[k]) || 'related'
    },
    toggle (r, reason, on) {
      if (on) {
        const cur = this.selected[r.idperson]
        // Un même contact coché dans deux onglets garde le signal le plus fort.
        const keep = cur && REASON_ORDER.indexOf(cur.reason) <= REASON_ORDER.indexOf(reason) ? cur.reason : reason
        this.$set(this.selected, r.idperson, { reason: keep, open_order: !!(r.open_order || (r.signals && r.signals.open_order)) })
      } else {
        this.$delete(this.selected, r.idperson)
      }
    },
    selectVisible (on) {
      const reason = this.tab === 'summary' ? null : { buyers: 'bought', quotes: 'quoted' }[this.tab] || this.tab
      this.pageRows.forEach(r => { if (this.selectable(r)) this.toggle(r, reason || this.bestReason(r), on) })
    },
    clearSelection () {
      this.selected = {}
    },
    openComposer () {
      this.composerSelection = Object.keys(this.selected).map(id => Object.assign({ idperson: Number(id) }, this.selected[id]))
      this.composerOpen = true
    },
    async onSent () {
      await Promise.all([this.loadOffers(), this.load(true)])
    },
    async loadOffers () {
      try {
        const { data } = await axios.get('/potential-interest/offers', { params: { idproduct: this.idproduct } })
        this.offers = data.offers || []
      } catch (e) {
        this.offers = []
      }
    },
    async load (keepSelection = false) {
      if (!this.idproduct) return
      this.loading = !keepSelection
      this.error = null
      if (!keepSelection) {
        this.selected = {}
        this.tab = 'summary'
        this.search = ''
      }
      try {
        const [{ data }] = await Promise.all([
          axios.get(`/potential-interest/product/${this.idproduct}`),
          this.loadOffers()
        ])
        this.data = data
      } catch (e) {
        const d = e.response && e.response.data
        this.error = (d && (d.message || d.detail)) || e.message || 'Could not compute the potential clients.'
      } finally {
        this.loading = false
      }
    },
    exportXlsx () {
      const rows = this.filteredRows
      if (!rows.length) return
      const base = r => ({ Client: r.name, Country: r.country_code || '', Language: (r.lang || '').toUpperCase(), 'Has e-mail': r.has_email ? 'yes' : 'no', Unsubscribed: r.unsubscribed ? 'yes' : '' })
      const map = {
        summary: r => Object.assign(base(r), {
          Score: Math.round(r.score * 100),
          Signals: REASON_ORDER.filter(k => r.signals[k]).map(k => REASON_LABELS[k]).join(', '),
          'Last activity': r.last_date || '', 'Last offer': r.last_offer_at || ''
        }),
        buyers: r => Object.assign(base(r), {
          Invoices: r.n_invoices, Paid: r.n_paid, Qty: r.qty, 'Amount EUR': r.amount, 'Last price': r.last_price,
          First: r.first_date, Last: r.last_date, 'Cadence (days)': r.reorder_interval_days || '', Reorder: r.reorder_status || ''
        }),
        quotes: r => Object.assign(base(r), {
          Quotations: r.n_quotes, 'Qty asked': r.qty, 'Last quotation': r.last_quote_id, Date: r.last_date,
          Status: r.last_status === 3 ? 'Cancelled' : 'Pending', 'Quoted price': r.last_price, 'Bought since': r.bought ? 'yes' : 'no'
        }),
        related: r => Object.assign(base(r), {
          Affinity: Math.round(r.relative * 100), 'Related products': r.n_reasons,
          'Because they bought': (r.reasons || []).map(x => `${x.category} ${x.name}`).join(' | ')
        }),
        similar: r => Object.assign(base(r), {
          Similarity: Math.round(r.best_sim * 100), 'Closest product': r.best ? `${r.best.category} ${r.best.name}` : '',
          'Similar products': r.n_similar, Qty: r.qty, 'Last purchase': r.last_date
        }),
        area: r => Object.assign(base(r), { Area: r.area, 'Buyers in area': r.buyers_in_area, 'Last activity': r.last_activity }),
        offers: o => ({ Sent: o.sent_at, Client: o.name, To: o.to_addr, Language: o.language, Zone: o.price_zone,
          Price: o.unit_price, Unit: o.price_unit, Why: o.reason, Status: o.status, Error: o.error || '', By: o.sent_by })
      }
      const sheet = XLSX.utils.json_to_sheet(rows.map(map[this.tab]))
      const book = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(book, sheet, this.tab)
      XLSX.writeFile(book, `potential_clients_${this.idproduct}_${this.tab}_${new Date().toISOString().slice(0, 10)}.xlsx`)
    }
  }
}
</script>

<style lang="scss">
.pi-clients {
  .pi-legend {
    max-width: 520px;
    opacity: 0.75;
  }

  .pi-scroll {
    overflow-x: auto;
    max-height: calc(100vh - 330px);
    overflow-y: auto;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 6px;
  }

  .pi-table {
    border-collapse: collapse;
    font-size: 0.88rem;

    th,
    td {
      padding: 0.4rem 0.5rem;
      border-bottom: 1px solid rgba(0, 0, 0, 0.08);
      vertical-align: top;
    }

    th {
      position: sticky;
      top: 0;
      z-index: 1;
      background: #fff;
      text-align: left;
      font-weight: 600;
      text-transform: uppercase;
      font-size: 0.7rem;
      white-space: nowrap;
    }
  }

  .pi-name {
    min-width: 200px;
  }

  .pi-emails {
    max-width: 260px;
    word-break: break-all;
  }

  .pi-signals .con-vs-chip,
  .pi-name .con-vs-chip,
  td .con-vs-chip {
    display: inline-flex;
    margin: 0 4px 4px 0;
  }

  .pi-bar {
    height: 6px;
    background: rgba(0, 0, 0, 0.08);
    border-radius: 3px;
    overflow: hidden;
  }

  .pi-bar-fill {
    height: 100%;
    background: rgba(var(--vs-primary), 1);
  }

  .pi-flag {
    display: inline-block;
    font-size: 0.7rem;
    padding: 0 6px;
    margin-right: 4px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.08);
  }

  .pi-flag--danger {
    background: rgba(var(--vs-danger), 0.15);
    color: rgba(var(--vs-danger), 1);
  }

  .pi-selection-bar {
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
  }
}

.theme-dark .pi-clients .pi-table th {
  background: #262c49;
}
</style>
